import type { AvailabilityTimeEncoding } from '@ooh/contracts';

/**
 * The encodings a manual operator override can switch to. The original 3
 * (`global`/`date_range`/`period_label_map`) are what the generic fallback
 * scorer's low-confidence guess can land on. `month` was added for
 * CU-86cb1npz2 (gap #2): manual from-scratch construction of a
 * `periodColumns` table for files whose period headers don't auto-parse
 * (e.g. Roman-numeral month abbreviations) — such a file still resolves via
 * the generic fallback (never the dedicated month-matrix resolver, which
 * requires >=3 parseable date-like headers), so the operator reaches
 * `month` only by picking it here, starting from an empty `periodColumns`.
 * `half_month` is deliberately NOT offered as a separate value — every
 * downstream consumer treats month/half_month identically, so this is one
 * user-facing option, not two (see `TimeEncodingSelector`'s call site).
 *
 * Single source of truth shared by `TimeEncodingSelector` (default render
 * options) and `usePreviewProfileEditor`'s `setTimeEncoding` (valid switch
 * targets, via `canOverrideTimeEncoding`) — do not redeclare this list in
 * either consumer.
 */
export const MANUALLY_SELECTABLE_ENCODINGS = [
  'global',
  'date_range',
  'period_label_map',
  'month',
] as const;

export type ManuallySelectableTimeEncoding = (typeof MANUALLY_SELECTABLE_ENCODINGS)[number];

export function isManuallySelectableEncoding(
  encoding: AvailabilityTimeEncoding,
): encoding is ManuallySelectableTimeEncoding {
  return (MANUALLY_SELECTABLE_ENCODINGS as readonly string[]).includes(encoding);
}
